/**
 * Utility functions for persisting and retrieving device preferences
 */

const STORAGE_KEY_PREFIX = 'meet_device_preferences_';

export interface DevicePreferences {
  videoDeviceId: string;
  audioInputDeviceId: string;
  audioOutputDeviceId: string;
}

const STORAGE_KEY = `${STORAGE_KEY_PREFIX}default`;

/**
 * Saves device preferences to localStorage
 */
export function saveDevicePreferences(preferences: DevicePreferences): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
      console.log('💾 Device preferences saved:', preferences);
    }
  } catch (error) {
    console.warn('Failed to save device preferences:', error);
  }
}

/**
 * Loads device preferences from localStorage
 */
export function loadDevicePreferences(): DevicePreferences | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const preferences = JSON.parse(stored) as DevicePreferences;
        console.log('💾 Device preferences loaded:', preferences);
        return preferences;
      }
    }
  } catch (error) {
    console.warn('Failed to load device preferences:', error);
  }
  return null;
}

/**
 * Clears saved device preferences
 */
export function clearDevicePreferences(): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(STORAGE_KEY);
      console.log('💾 Device preferences cleared');
    }
  } catch (error) {
    console.warn('Failed to clear device preferences:', error);
  }
}

/**
 * Validates that the saved device preferences are still available in the device list
 */
export function validateDevicePreferences(
  preferences: DevicePreferences | null,
  availableDevices: {
    videoDevices: MediaDeviceInfo[];
    audioInputDevices: MediaDeviceInfo[];
    audioOutputDevices: MediaDeviceInfo[];
  }
): DevicePreferences | null {
  if (!preferences) return null;

  const validated: DevicePreferences = {
    videoDeviceId: '',
    audioInputDeviceId: '',
    audioOutputDeviceId: ''
  };

  // Validate video device
  const videoDevice = availableDevices.videoDevices.find(
    d => d.deviceId === preferences.videoDeviceId
  );
  if (videoDevice) {
    validated.videoDeviceId = preferences.videoDeviceId;
  } else if (availableDevices.videoDevices.length > 0) {
    // Fallback to first available device
    validated.videoDeviceId = availableDevices.videoDevices[0].deviceId;
    console.log('⚠️ Saved video device not found, using default');
  }

  // Validate audio input device
  const audioInputDevice = availableDevices.audioInputDevices.find(
    d => d.deviceId === preferences.audioInputDeviceId
  );
  if (audioInputDevice) {
    validated.audioInputDeviceId = preferences.audioInputDeviceId;
  } else if (availableDevices.audioInputDevices.length > 0) {
    // Fallback to first available device
    validated.audioInputDeviceId = availableDevices.audioInputDevices[0].deviceId;
    console.log('⚠️ Saved audio input device not found, using default');
  }

  // Validate audio output device
  const audioOutputDevice = availableDevices.audioOutputDevices.find(
    d => d.deviceId === preferences.audioOutputDeviceId
  );
  if (audioOutputDevice) {
    validated.audioOutputDeviceId = preferences.audioOutputDeviceId;
  } else if (availableDevices.audioOutputDevices.length > 0) {
    // Fallback to first available device
    validated.audioOutputDeviceId = availableDevices.audioOutputDevices[0].deviceId;
    console.log('⚠️ Saved audio output device not found, using default');
  }

  return validated;
}



